import React, { useState, useEffect, useRef } from 'react';
import './doctor-add.scss';
import { Modal, Select, Tabs, Upload, Input, Form, Row, Col, message, Checkbox } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { useTranslation, Button } from 'soxo-bootstrap-core';
import Doctor from '../../../../models/doctor';

const doctorApi = new Doctor();

const DoctorAdd = ({ visible, onCancel, attributes, doctorId, doctorData, onSuccess }) => {
    const [form] = Form.useForm();
    const { t } = useTranslation();

    const [fileUrl, setFileUrl] = useState(null);
    const [uploading] = useState(false);
    const [loading, setLoading] = useState(false);

    const propValues = attributes || {};
    const doctorType = propValues?.type;
    const editMode = Boolean(doctorId);

    const [codeStatus, setCodeStatus] = useState({
        status: '',
        message: '',
    });

    //  Auto-focus ref
    const nameInputRef = useRef(null);

    //  Auto-focus when modal opens
    useEffect(() => {
        if (visible) {
            setTimeout(() => {
                nameInputRef.current?.focus();
            }, 200);
        }
    }, [visible]);

    //  Enter key -> go to next input
    const handleEnterKey = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const fields = Array.from(document.querySelectorAll('.ant-input, .ant-select-selector'));
            const index = fields.indexOf(e.target);

            if (index > -1 && index < fields.length - 1) {
                const nextField = fields[index + 1];
                nextField.focus();
                if (nextField.classList.contains('ant-select-selector')) {
                    nextField.click();
                }
            }
        }
    };

    const fillEditForm = (data) => {
        if (!data) return;

        form.setFieldsValue({
            name: data.name,
            code: data.code,
            qualification: data.qualification,
            designation: data.designation,
            type: data.type,
            regno1: data.regno1,
            remarks: data.remarks,
            active: data.active === 'Y' ? 'Y' : 'N',
            address: data.address1,
            places: data.place,
            pincode: data.zip,
            phone_number: data.phone,
            alternate_phone: data.mobile,
            email_id: data.email,
            fax: data.fax,
        });

        if (data.signature) {
            setFileUrl(data.signature);
            form.setFieldValue('signature', data.signature);
        }
    };

    const validateDoctorCode = async (_, value) => {
        if (editMode) {
            return Promise.resolve(); // <-- Skip validation completely in edit mode
        }
        if (!value) {
            setCodeStatus({ status: '', message: '' });
            return Promise.resolve();
        }

        if (editMode && doctorData?.do_code === value) {
            setCodeStatus({ status: 'success', message: 'Code is unchanged' });
            return Promise.resolve();
        }

        try {
            const res = await doctorApi.getDoctorCode(value);

            if (res?.statusCode === 409 || res?.success === false) {
                setCodeStatus({ status: 'error', message: res.message || 'Code already exists' });
                return Promise.reject(new Error(res.message || 'Code already exists'));
            }

            if (res?.statusCode === 200 && res?.success === true) {
                setCodeStatus({
                    status: 'success',
                    message: 'The entered code is used for assigning a doctor.',
                });
                return Promise.resolve();
            }

            setCodeStatus({ status: 'error', message: 'Unexpected response' });
            return Promise.reject(new Error('Unexpected response'));
        } catch (err) {
            setCodeStatus({ status: 'error', message: 'Validation failed' });
            return Promise.reject(new Error('Validation failed'));
        }
    };

    useEffect(() => {
        if (visible) {
            if (editMode) {
                fillEditForm(doctorData);
            }
        } else {
            form.resetFields();
            setFileUrl(null);
        }
    }, [visible, editMode, doctorData]);

    const uploadProps = {
        name: 'file',
        accept: 'image/png',
        showUploadList: false,
        beforeUpload: (file) => {
            if (file.type !== 'image/png') {
                message.error('You can only upload PNG files!');
                return Upload.LIST_IGNORE;
            }
            if (file.size / 1024 / 1024 >= 1) {
                message.error('Image must be smaller than 1MB!');
                return Upload.LIST_IGNORE;
            }

            const reader = new FileReader();
            reader.onload = () => {
                setFileUrl(reader.result);
                form.setFieldValue('signature', reader.result);
            };
            reader.readAsDataURL(file);

            message.success('Signature uploaded successfully!');
            return false;
        },
    };

    const handleFinish = async (values) => {
        setLoading(true);

        const payload = {
            dbkey: values.dbkey ? Number(values.dbkey) : null,
            code: values.code || '',
            name: values.name || '',
            regno1: values.regno1 || '',
            regno2: '',
            qualification: values.qualification || '',
            desg: values.designation || '',
            add1: values.address || '',
            add2: '',
            place: values.places || '',
            phone: values.phone_number || '',
            fax: values.fax || '',
            mobile: values.alternate_phone || '',
            email: values.email_id || '',
            remarks: values.remarks || '',
            deptptr: '',
            regfee: values.regfee ? Number(values.regfee) : null,
            consfee: values.consfee ? Number(values.consfee) : null,
            gpfee: values.gpfee ? Number(values.gpfee) : null,
            consdays: values.consdays ? Number(values.consdays) : null,
            slno: values.slno ? Number(values.slno) : null,
            active: values.active === 'Y' ? 'Y' : 'N',
            timeperpatient: values.timeperpatient ? Number(values.timeperpatient) : null,
            username: '',
            type: values.type || '',
            signature: fileUrl || '',
            zip: values.pincode || '',
        };

        try {
            let res;
            if (editMode) {
                res = await doctorApi.updateDoctors(payload, doctorData.code);
            } else {
                res = await doctorApi.createDoctors(payload);
            }

            if (res?.success) {
                message.success(editMode ? 'Doctor updated successfully' : 'Doctor created successfully');
                onSuccess?.();
                onCancel();
            } else {
                message.error(res?.message || 'Operation failed');
            }
        } catch (e) {
            message.error('Something went wrong');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={doctorId ? 'Edit Doctor' : 'Add Doctor'}
            open={visible}
            onCancel={onCancel}
            width={950}
            footer={null}
            destroyOnClose
            style={{ top: 10 }}
        >
            <Form form={form} layout="vertical" onFinish={handleFinish}>
                {/* NAME + CODE */}
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item label="Name" name="name" rules={[{ required: true }]}>
                            <Input autoComplete="off" ref={nameInputRef} placeholder="Enter Name" onKeyDown={handleEnterKey} />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            label="Code"
                            name="code"
                            validateTrigger="onChange"
                            hasFeedback={!editMode}
                            // validateStatus={!editMode ? codeStatus.status : ''}
                            // help={!editMode ? codeStatus.message : ''}
                            // validateStatus={codeStatus.status}
                            // help={codeStatus.message}
                            rules={[{ required: true, message: 'Please enter code' }, { validator: validateDoctorCode }]}
                        >
                            <Input
                                autoComplete="off"
                                placeholder="Enter Code"
                                onKeyDown={handleEnterKey}
                                disabled={editMode} // <-- Proper disable
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Tabs defaultActiveKey="1">
                    {/* GENERAL TAB */}
                    <Tabs.TabPane tab="GENERAL" key="1">
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item label="Type" name="type">
                                    <Select placeholder="Select Type" options={doctorType || []} showSearch />
                                </Form.Item>
                            </Col>

                            <Col span={8}>
                                <Form.Item label="Qualification" name="qualification">
                                    <Input autoComplete="off" placeholder="Enter Qualification" onKeyDown={handleEnterKey} />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item label="Designation" name="designation">
                                    <Input autoComplete="off" placeholder="Enter Designation" onKeyDown={handleEnterKey} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={6}>
                                <Form.Item label="Reg. No." name="regno1">
                                    <Input autoComplete="off" placeholder="Enter Reg No" onKeyDown={handleEnterKey} />
                                </Form.Item>
                            </Col>

                            <Col span={8}>
                                <Form.Item label="Remarks" name="remarks">
                                    <Input autoComplete="off" placeholder="Enter Remarks" onKeyDown={handleEnterKey} />
                                </Form.Item>
                            </Col>

                            <Col span={8}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 30 }}>
                                    <label>Doctor Status</label>
                                    <Form.Item
                                        name="active"
                                        valuePropName="checked"
                                        getValueFromEvent={(e) => (e.target.checked ? 'Y' : 'N')}
                                        getValueProps={(value) => ({ checked: value === 'Y' })}
                                        style={{ marginBottom: 0 }}
                                    >
                                        <Checkbox>Active</Checkbox>
                                    </Form.Item>
                                </div>
                            </Col>
                        </Row>

                        {/* SIGNATURE */}
                        <Row gutter={16} align="middle">
                            <Col span={10}>
                                <Form.Item
                                    label="Add Signature"
                                    name="signature"
                                    rules={[{ required: true, message: t('Please upload signature') }]}
                                    style={{ marginTop: '30px' }}
                                >
                                    <div className="signature-upload-container">
                                        <Upload {...uploadProps}>
                                            {fileUrl ? (
                                                <img src={fileUrl} alt="Signature Preview" className="signature-preview" />
                                            ) : (
                                                <div className="upload-placeholder">
                                                    <p className="ant-upload-text">Drag & drop your signature image here</p>
                                                    <Button icon={<InboxOutlined />} type="primary" loading={uploading} className="upload-btn">
                                                        Select Image
                                                    </Button>
                                                </div>
                                            )}
                                        </Upload>
                                    </div>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Tabs.TabPane>

                    {/* ADDRESS TAB */}
                    <Tabs.TabPane tab="ADDRESS" key="2">
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item label="Address" name="address">
                                    <Input autoComplete="off" placeholder="Enter Address" onKeyDown={handleEnterKey} />
                                </Form.Item>
                            </Col>

                            <Col span={8}>
                                <Form.Item label="Places" name="places">
                                    <Input autoComplete="off" placeholder="Enter Place" onKeyDown={handleEnterKey} />
                                </Form.Item>
                            </Col>

                            <Col span={8}>
                                <Form.Item label="Pincode" name="pincode">
                                    <Input autoComplete="off" placeholder="Enter Pincode" onKeyDown={handleEnterKey} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item label="Phone Number" name="phone_number">
                                    <Input autoComplete="off" placeholder="Enter Phone Number" onKeyDown={handleEnterKey} />
                                </Form.Item>
                            </Col>

                            <Col span={8}>
                                <Form.Item label="Alternate Phone" name="alternate_phone">
                                    <Input autoComplete="off" placeholder="Enter Alternate Number" onKeyDown={handleEnterKey} />
                                </Form.Item>
                            </Col>

                            <Col span={8}>
                                <Form.Item label="Email ID" name="email_id">
                                    <Input autoComplete="off" placeholder="Enter Email ID" onKeyDown={handleEnterKey} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item label="Fax" name="fax">
                                    <Input autoComplete="off" placeholder="Enter Fax Number" onKeyDown={handleEnterKey} />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Tabs.TabPane>
                </Tabs>

                {/* FOOTER BUTTONS */}
                <div className="form-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                    <Button onClick={onCancel} type="secondary">
                        Cancel
                    </Button>
                    <Button type="primary" htmlType="submit" loading={loading}>
                        {doctorId ? 'Update' : 'Save'}
                    </Button>
                </div>
            </Form>
        </Modal>
    );
};

export default DoctorAdd;
